import { ArrowRight } from 'lucide-react';
import { Fragment } from 'react';

function formatDiffValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length === 0 ? '""' : v;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// Most platform-side audit rows for tenant edits ship as `{before, after}`
// snapshots of the full Tenant row. Rendering that raw is two 18-field JSON
// blobs that an operator has to eyeball to spot the one field that changed,
// so collapse to just the changed keys when the shape matches. Other shapes
// (impersonate reason, suspend reason, etc.) fall through to JSON.
export function AuditDiff({ diff }: { diff: unknown }): JSX.Element {
  if (
    diff &&
    typeof diff === 'object' &&
    'before' in diff &&
    'after' in diff &&
    diff.before &&
    diff.after &&
    typeof diff.before === 'object' &&
    typeof diff.after === 'object'
  ) {
    const before = diff.before as Record<string, unknown>;
    const after = diff.after as Record<string, unknown>;
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
    const changed = keys
      .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
      .sort();
    if (changed.length > 0) {
      return (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md border border-border-subtle bg-bg-subtle/40 p-2 text-xs">
          {changed.map((k) => (
            <Fragment key={k}>
              <dt className="font-mono text-fg-muted">{k}</dt>
              <dd className="flex items-baseline gap-1.5">
                <span className="font-mono text-fg-subtle line-through decoration-1">
                  {formatDiffValue(before[k])}
                </span>
                <ArrowRight className="h-3 w-3 flex-shrink-0 text-fg-muted rtl:rotate-180" aria-hidden />
                <span className="font-mono text-fg">{formatDiffValue(after[k])}</span>
              </dd>
            </Fragment>
          ))}
        </dl>
      );
    }
  }
  return (
    <pre className="mt-2 max-h-48 overflow-auto rounded-md border border-border-subtle bg-bg-subtle/40 p-2 text-xs">
      {JSON.stringify(diff, null, 2)}
    </pre>
  );
}
